// src/components/FacebookRedirectLogin.js
import React, { useMemo, useState, useCallback } from "react";
import FacebookIcon from "@mui/icons-material/Facebook";
import { Button, Box, Snackbar, Alert } from "@mui/material";
import { useTranslation } from "react-i18next";

const trimTrailingSlash = (value) => String(value || "").replace(/\/+$/, "");

const FacebookRedirectLogin = ({
  onSuccess, // (δεν χρησιμοποιείται εδώ, το success γίνεται στο callback route)
  onFailure,
  children,
  sx,
  postAuthRedirect,
}) => {
  const { t } = useTranslation();
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // locale/country prefix από το path (πχ /el, /en)
  const match =
    typeof window !== "undefined"
      ? window.location.pathname.match(/^\/([a-z]{2}(?:-[a-z]{2})?)\b/)
      : null;
  const country = match ? match[1] : "el";

  // redirect URI που πρέπει να ταιριάζει 100% με Facebook console + backend flow
  const redirectUri =
    typeof window !== "undefined"
      ? `${window.location.origin}/${country}/auth/facebook/callback`
      : "";

  // PWA / standalone
  const isStandalone =
    typeof window !== "undefined" &&
    (window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true);

  // In-app browser / webview detection
  const ua = (typeof navigator !== "undefined" ? navigator.userAgent : "") || "";
  const isWebView =
    /(FBAN|FBAV|FB_IAB|Instagram|Line\/|MicroMessenger|wv\)|; wv)/i.test(ua) ||
    (typeof window !== "undefined" && window.ReactNativeWebView);

  const loginPageUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/${country}/login`
      : "";

  const usersApiBase = trimTrailingSlash(
    process.env.REACT_APP_USERS_ROUTES_API_BASE_URL
  );

  // Start URL για OAuth start endpoint (backend)
  const startUrl = useMemo(() => {
    if (!usersApiBase || !redirectUri) return null;
    return `${usersApiBase}/login/facebook/start?redirect_uri=${encodeURIComponent(
      redirectUri
    )}`;
  }, [usersApiBase, redirectUri]);

  const handlePreClick = useCallback(
    (e) => {
      // Μπλόκαρε σε PWA/in-app browsers και δείξε οδηγία
      if (isStandalone || isWebView) {
        e.preventDefault();
        const msg =
          t("pwa_facebook_login_not_supported") ||
          "Facebook login is not supported inside in-app browsers. Please open InCooking in Chrome or Safari.";
        setSnackbarMessage(`${msg} ${loginPageUrl}`);
        setOpenSnackbar(true);
        return;
      }

      // Αποθήκευση postAuthRedirect πριν φύγει ο χρήστης στο Facebook
      if (postAuthRedirect && typeof window !== "undefined") {
        try {
          window.sessionStorage.setItem("postAuthRedirect", postAuthRedirect);
        } catch (_err) {
          // non-blocking
        }
      }

      // Αν δεν έχει ρυθμιστεί το API base, μην πας πουθενά
      if (!startUrl) {
        e.preventDefault();
        const msg =
          "Facebook login is temporarily unavailable (missing API base URL).";
        setSnackbarMessage(msg);
        setOpenSnackbar(true);
        if (onFailure) onFailure(new Error(msg));
      }
    },
    [
      isStandalone,
      isWebView,
      postAuthRedirect,
      startUrl,
      loginPageUrl,
      onFailure,
      t,
    ]
  );

  const openInBrowser = () => {
    // Προσπάθεια να ανοίξει σε νέο tab — αν μπλοκάρει, κάνει redirect στο ίδιο tab
    const popup =
      typeof window !== "undefined"
        ? window.open(loginPageUrl, "_blank", "noopener,noreferrer")
        : null;
    if (!popup && typeof window !== "undefined") {
      window.location.assign(loginPageUrl);
    }
  };

  return (
    <Box sx={{ width: "100%", ...sx }}>
      <Button
        fullWidth
        variant="contained"
        component="a"
        href={startUrl || "#"}
        onClick={handlePreClick}
        startIcon={<FacebookIcon />}
        sx={{
          bgcolor: "#4267B2",
          color: "#fff",
          fontWeight: 600,
          fontSize: "1.07rem",
          borderRadius: 2,
          boxShadow: 2,
          minHeight: 44,
          "&:hover": { bgcolor: "#37508b" },
        }}
        aria-label={t("login_with_facebook") || "Σύνδεση με Facebook"}
      >
        {children || t("login_with_facebook") || "Σύνδεση με Facebook"}
      </Button>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={8000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert
          severity="warning"
          onClose={() => setOpenSnackbar(false)}
          action={
            <Button color="inherit" size="small" onClick={openInBrowser}>
              Open in browser
            </Button>
          }
          sx={{ width: "100%" }}
        >
          {snackbarMessage ||
            t("pwa_facebook_login_not_supported") ||
            "Please open in Chrome/Safari."}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FacebookRedirectLogin;